import { ArrowDownTrayIcon, ArrowPathIcon, TrashIcon } from "@heroicons/react/24/solid";
import { Spinner } from "../../utils/Spinner";
import { RecordingTranscriptPair } from "../../utils/SessionUtils";
import { WarningBanner } from "../common/WarningBanner";
import { ErrorBanner } from "../common/ErrorBanner";
import { downloadFile } from "../../utils/RecordingUtils";
import { useVetRec } from "../../providers/VetRecProvider";
import { getFileExtensionFromUrl } from "../../utils/TranscriptUtils";
import VetRecButton from "../common/VetRecButton";
import { useRef, useState } from "react";
import { formatTranscript } from "@neum/frontend-shared/utils/textUtils";
import { Transcript } from "./TranscriptQuill";
import { useHistory } from "../../providers/HistoryProvider";

interface RecordingTranscriptPairBodyProps {
    recordingTranscriptPair: RecordingTranscriptPair;
    downloadFileName: string;
    source?: string;
    processRecordings?: () => Promise<void>;
    handleDeleteClick?: () => void;
}

export const RecordingTranscriptPairBody: React.FC<RecordingTranscriptPairBodyProps> = ({ 
    recordingTranscriptPair, 
    downloadFileName, 
    source,
    processRecordings,
    handleDeleteClick
}) => {
    const recordingUrl = recordingTranscriptPair.recording_url;
    const isPrimaryRecordingAvailable = !!recordingUrl;
    const transcriptStatus: string = recordingTranscriptPair.transcript_status;
    const transcript_id = recordingTranscriptPair.transcript_id;
    const { activeSession } = useHistory()
    const { isImpersonation } = useVetRec()

    const [reprocessLoading, setReprocessLoading] = useState(false);
    const audioRef = useRef<HTMLAudioElement>(null);
    
    async function handleClickReprocess() {
        if (processRecordings) {
            try {
                setReprocessLoading(true);
                await processRecordings();
            } finally {
                setReprocessLoading(false);
            }
        }
    }

    const setCurrentTime = (time_s: number) => {
        if (audioRef.current) {
            audioRef.current.currentTime = time_s;
            audioRef.current.play().catch(() => {});
        }
    }
    
    return (
        <div className="px-6 pb-4 pt-4 text-sm text-gray-500 border border-t-0 border-gray-300 rounded-b-lg shadow space-y-2">
            {isImpersonation && <div className="px-4 py-2 flex flex-col items-center bg-yellow-100 rounded-md text-center text-sm text-yellow-900 border border-yellow-700 border-dashed">
                <span>Recording ID: {recordingTranscriptPair.recording_id}</span>
                {transcript_id && <span>Transcript ID: {transcript_id}</span>}
            </div>}
            {isPrimaryRecordingAvailable ? <div className="flex flex-row items-center gap-x-2">
                <audio key={recordingUrl} controls src={recordingUrl} className="w-full my-4 fill-green-600" ref={audioRef}>
                    Your browser does not support the audio element.
                </audio>
                <VetRecButton onClick={() => downloadFile(recordingUrl, downloadFileName + "." + getFileExtensionFromUrl(recordingUrl))} className="text-white p-2 bg-blue-600 rounded-md"><ArrowDownTrayIcon className="h-5 w-5" /></VetRecButton>
                {handleDeleteClick && <VetRecButton disabled={transcriptStatus === "Processing"} onClick={handleDeleteClick} className={`text-white p-2 bg-red-500 hover:bg-red-400 rounded-md ${transcriptStatus === "Processing" ? "opacity-50 cursor-not-allowed" : ""}`}><TrashIcon className="h-5 w-5" /></VetRecButton>}
            </div> : (transcriptStatus !== "FailedNoData" && <div className="w-full text-center">
                <WarningBanner warningText="We are unable to display this recording. For access to the raw recordings contact support@vetrec.io" />
            </div>)}
            {transcriptStatus === "Not Started" && <div className="w-full text-center">
                There is no transcript for this recording. Please process the visit to see the transcript.
            </div>}
            {transcriptStatus === "Processing" && <div className='pt-5 w-full m-auto flex flex-col justify-center items-center gap-y-6 mb-8 font-semibold'>
                <div>Transcribing recording 🤖...</div>
                <Spinner size="w-12 h-12" timer={true}/>
                <div className="text-sm text-center text-gray-700 font-normal">Transcript will be ready within 60s.<br/>Feel free to navigate away if you need to.</div>
            </div>}
            {["FailedNoData", "Failed"].includes(transcriptStatus) && <div className="flex flex-row gap-x-2">
                {transcriptStatus === "FailedNoData" && <WarningBanner warningText={`Your recording has not yet been uploaded. ${(source && ["ios", "android"].includes(source)) ? "Please confirm the recording has been uploaded from your phone app and re-process." : "Please try again in a few seconds."} Reach out to support for any assistance.`} />}
                {transcriptStatus === "Failed" && <ErrorBanner errorText="There was an issue transcribing this recording. Re-process your visit to try again." />}
                {processRecordings && <VetRecButton 
                    onClick={handleClickReprocess} 
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    disabled={reprocessLoading}
                >
                    <ArrowPathIcon className="mr-2 h-4 w-4" />
                    Reprocess
                    {reprocessLoading && <Spinner size="w-4 h-4" timer={true} />}
                </VetRecButton>}
            </div>}
            {transcriptStatus === "Completed" && <Transcript
                setCurrentTime={setCurrentTime}
                startTimestamp={recordingTranscriptPair.recording_timestamp ? (new Date(recordingTranscriptPair.recording_timestamp)).toLocaleString() : undefined}
                entries={formatTranscript(recordingTranscriptPair.transcript_content, recordingTranscriptPair.recording_timestamp ? (new Date(recordingTranscriptPair.recording_timestamp)).toLocaleString() : undefined, activeSession?.spoken_language ?? "English")}
            />}
        </div>
    );
};
